import type { SvgIconComponent } from '@mui/icons-material';
import PersonSearchIcon from '@mui/icons-material/PersonSearchOutlined';
import LeaderboardIcon from '@mui/icons-material/LeaderboardOutlined';
import HowToRegIcon from '@mui/icons-material/HowToRegOutlined';
import SchoolIcon from '@mui/icons-material/SchoolOutlined';
import ApprovalIcon from '@mui/icons-material/ApprovalOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import LuggageIcon from '@mui/icons-material/LuggageOutlined';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoffOutlined';
import FlightLandIcon from '@mui/icons-material/FlightLandOutlined';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttleOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalanceOutlined';
import PublicIcon from '@mui/icons-material/PublicOutlined';
import type { ReportType } from '../../api/reports';

/** Which filters each report's API applies (backend reports.service.ts). */
export type ReportFilterKey =
  | 'dateRange'
  | 'branch'
  | 'counselor'
  | 'university'
  | 'intake'
  | 'leadStatus'
  | 'leadSource'
  | 'paymentCategory'
  | 'paymentStatus'
  | 'applicationStatus'
  | 'visaStatus';

export interface ReportDefinition {
  type: ReportType;
  name: string;
  description: string;
  icon: SvgIconComponent;
  filters: ReportFilterKey[];
  /** Which date the reporting period applies to. */
  dateLabel?: string;
}

export interface ReportCategory {
  key: string;
  title: string;
  description: string;
  color: string;
  reports: ReportDefinition[];
}

export const REPORT_CATEGORIES: ReportCategory[] = [
  {
    key: 'leads',
    title: 'Leads & Counselling',
    description: 'Enquiries, lead pipeline and counsellor performance.',
    color: '#5B2DA3',
    reports: [
      {
        type: 'leads',
        name: 'Lead Report',
        description: 'All leads with source, status, ad and assigned counsellor.',
        icon: PersonSearchIcon,
        filters: ['dateRange', 'branch', 'counselor', 'leadStatus', 'leadSource'],
        dateLabel: 'Date created',
      },
      {
        type: 'counselor-performance',
        name: 'Counsellor Performance',
        description: 'Leads assigned and converted per counsellor, with conversion rate.',
        icon: LeaderboardIcon,
        filters: [],
      },
    ],
  },
  {
    key: 'students',
    title: 'Students & Admissions',
    description: 'Registrations, university applications and visas.',
    color: '#12B76A',
    reports: [
      {
        type: 'registrations',
        name: 'Registration Report',
        description: 'Registered students with their source lead and registering counsellor.',
        icon: HowToRegIcon,
        filters: ['dateRange', 'branch', 'counselor', 'university', 'intake'],
        dateLabel: 'Date registered',
      },
      {
        type: 'admissions',
        name: 'Admission Report',
        description: 'Applications with offer letter and COE milestones.',
        icon: SchoolIcon,
        filters: ['dateRange', 'branch', 'university', 'intake', 'applicationStatus'],
        dateLabel: 'Application date',
      },
      {
        type: 'visa',
        name: 'Visa Report',
        description: 'Applications with visa status, applied and decision dates.',
        icon: ApprovalIcon,
        filters: ['dateRange', 'branch', 'university', 'intake', 'visaStatus'],
        dateLabel: 'Application date',
      },
    ],
  },
  {
    key: 'finance',
    title: 'Finance',
    description: 'Payments recorded against students.',
    color: '#F79009',
    reports: [
      {
        type: 'payments',
        name: 'Payment Report',
        description: 'Payments with category, amount, status and approver.',
        icon: PaymentsIcon,
        filters: ['dateRange', 'branch', 'paymentCategory', 'paymentStatus'],
        dateLabel: 'Payment date',
      },
    ],
  },
  {
    key: 'travel',
    title: 'Travel & Departures',
    description: 'Ready to fly, departures, arrivals and airport pickups.',
    color: '#0E7490',
    reports: [
      {
        type: 'travel',
        name: 'Travel Report',
        description: 'Ready-to-fly, departed and arrived students with batch and dates.',
        icon: LuggageIcon,
        filters: ['dateRange', 'branch', 'university', 'intake'],
        dateLabel: 'Application date',
      },
      {
        type: 'departure',
        name: 'Departure Report',
        description: 'Students with a departure date and their flight details.',
        icon: FlightTakeoffIcon,
        filters: ['dateRange', 'branch', 'university', 'intake'],
        dateLabel: 'Departure date',
      },
      {
        type: 'arrival',
        name: 'Arrival Report',
        description: 'Students with an arrival date and accommodation status.',
        icon: FlightLandIcon,
        filters: ['dateRange', 'branch', 'university', 'intake'],
        dateLabel: 'Arrival date',
      },
      {
        type: 'pickup',
        name: 'Pickup Report',
        description: 'Students who need an airport pickup, and whether one is assigned.',
        icon: AirportShuttleIcon,
        filters: ['branch', 'university', 'intake'],
      },
      {
        type: 'university',
        name: 'University-wise Travel',
        description: 'Ready, departed and arrived students per university.',
        icon: AccountBalanceIcon,
        filters: ['branch', 'university', 'intake'],
      },
      {
        type: 'country',
        name: 'Country-wise Travel',
        description: 'Ready, departed and arrived students per destination country.',
        icon: PublicIcon,
        filters: ['branch', 'intake'],
      },
    ],
  },
];

export const FILTER_LABELS: Record<ReportFilterKey, string> = {
  dateRange: 'Date range',
  branch: 'Branch',
  counselor: 'Counsellor',
  university: 'University',
  intake: 'Intake',
  leadStatus: 'Lead status',
  leadSource: 'Lead source',
  paymentCategory: 'Payment category',
  paymentStatus: 'Payment status',
  applicationStatus: 'Application status',
  visaStatus: 'Visa status',
};

export const PAYMENT_STATUSES = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

export const APPLICATION_STATUS_OPTIONS = [
  'APPLIED',
  'OFFER_RECEIVED',
  'OFFER_ACCEPTED',
  'CONDITIONAL_OFFER',
  'UNCONDITIONAL_OFFER',
  'COE_ISSUED',
  'VISA_LODGED',
  'VISA_APPROVED',
  'VISA_REJECTED',
  'READY_TO_FLY',
  'DEPARTED',
  'ARRIVED',
  'DEFERRED',
  'WITHDRAWN',
  'REJECTED',
].map((value) => ({
  value,
  label: value
    .split('_')
    .map((word, i) => (word === 'COE' ? word : i === 0 ? word.charAt(0) + word.slice(1).toLowerCase() : word.toLowerCase()))
    .join(' '),
}));
